/**
 * Stock Costing Hook
 * 
 * React hook for managing stock costing calculations and updates.
 * Integrates with the costing service to update stock items when movements occur.
 * Supports both Average Cost and FIFO valuation methods.
 */

import { useCallback } from 'react';
import { StockItem } from '@/types/stock-item';
import {
  calculateAverageCost,
  calculateCOGS,
  calculateMargin,
  calculateMarkup,
  calculateInventoryValue,
  addFIFOCostLayer,
  calculateFIFOCOGS,
  getCurrentFIFOCost,
  getFIFOWeightedAverage,
} from '@/services/costing-service';
import { useValuationMethod } from './use-valuation-method';

export interface StockCostingResult {
  newAverageCost: number;
  cogs: number;
  margin: number;
  markup: number;
  inventoryValue: number;
}

export interface FIFOCostingResult {
  newAverageCost: number;
  totalCost: number;
  averageCostForTransaction: number;
  layersUsed?: any[];
  remainingLayers?: any[];
}

export const useStockCosting = () => {
  const { valuationMethod } = useValuationMethod();
  /**
   * Process a purchase receipt and update average cost
   * Automatically uses FIFO or Average Cost based on valuation method setting
   */
  const processPurchaseReceipt = useCallback((
    stockItem: StockItem,
    quantityReceived: number,
    unitCost: number,
    warehouseCode?: string,
    locationCode?: string,
    reference?: string
  ): Partial<StockItem> => {
    let newAverageCost: number;

    // Use FIFO or Average Cost based on valuation method
    if (valuationMethod === 'fifo') {
      // Add FIFO cost layer
      addFIFOCostLayer(
        stockItem.code,
        warehouseCode || 'DEFAULT',
        locationCode,
        quantityReceived,
        unitCost,
        reference
      );
      
      // Calculate weighted average for reporting (FIFO still tracks this)
      newAverageCost = calculateAverageCost(
        stockItem.physicalQuantity,
        stockItem.averageCost,
        quantityReceived,
        unitCost
      );
    } else {
      // Average Cost method
      newAverageCost = calculateAverageCost(
        stockItem.physicalQuantity,
        stockItem.averageCost,
        quantityReceived,
        unitCost
      );
    }

    const newQuantity = stockItem.physicalQuantity + quantityReceived;
    const inventoryValue = calculateInventoryValue(newQuantity, newAverageCost);

    return {
      averageCost: newAverageCost,
      lastCostPrice: unitCost,
      physicalQuantity: newQuantity,
      lastPurchaseDate: new Date(),
      lastPurchaseQuantity: quantityReceived,
      lastPurchasePrice: unitCost,
    };
  }, [valuationMethod]);

  /**
   * Process a sales transaction and calculate COGS
   * Automatically uses FIFO or Average Cost based on valuation method setting
   */
  const processSale = useCallback((
    stockItem: StockItem,
    quantitySold: number,
    sellingPrice: number,
    warehouseCode?: string
  ): Partial<StockItem> & { cogs: number; margin: number; markup: number; costPerUnit: number; layersUsed?: any[] } => {
    let cogs: number;
    let costPerUnit: number;
    let layersUsed: any[] | undefined;

    // Use FIFO or Average Cost based on valuation method
    if (valuationMethod === 'fifo') {
      // Calculate FIFO COGS
      const fifoResult = calculateFIFOCOGS(
        stockItem.code,
        warehouseCode || 'DEFAULT',
        quantitySold
      );
      
      cogs = fifoResult.totalCost;
      costPerUnit = fifoResult.averageCost;
      layersUsed = fifoResult.layersUsed;
    } else {
      // Average Cost method
      cogs = calculateCOGS(quantitySold, stockItem.averageCost);
      costPerUnit = stockItem.averageCost;
    }

    const margin = calculateMargin(sellingPrice, costPerUnit);
    const markup = calculateMarkup(sellingPrice, costPerUnit);
    
    const newQuantity = stockItem.physicalQuantity - quantitySold;
    const totalSalesValue = quantitySold * sellingPrice;

    return {
      physicalQuantity: newQuantity,
      cogs,
      margin,
      markup,
      costPerUnit,
      layersUsed,
      // Note: These fields would need to be added to StockItem type
      // totalCostOfSales: (stockItem.totalCostOfSales || 0) + cogs,
      // totalValueOfSales: (stockItem.totalValueOfSales || 0) + totalSalesValue,
    };
  }, [valuationMethod]);

  /**
   * Process a stock adjustment (write-off, correction, etc.)
   * Average cost doesn't change, only quantity
   */
  const processAdjustment = useCallback((
    stockItem: StockItem,
    quantityAdjustment: number
  ): Partial<StockItem> => {
    const newQuantity = stockItem.physicalQuantity + quantityAdjustment;
    
    return {
      physicalQuantity: Math.max(0, newQuantity), // Prevent negative stock
      // Average cost remains the same
    };
  }, []);

  /**
   * Process a stock transfer between locations
   * Average cost moves with the stock
   */
  const processTransfer = useCallback((
    stockItem: StockItem,
    quantityTransferred: number
  ): Partial<StockItem> => {
    // For transfers, the average cost doesn't change
    // This is handled at the warehouse level
    return {
      // Average cost remains the same
      // Quantity changes are handled by warehouse-specific logic
    };
  }, []);

  /**
   * Calculate current margin for a stock item
   */
  const getCurrentMargin = useCallback((
    stockItem: StockItem
  ): number => {
    return calculateMargin(stockItem.unitPrice, stockItem.averageCost);
  }, []);

  /**
   * Calculate current markup for a stock item
   */
  const getCurrentMarkup = useCallback((
    stockItem: StockItem
  ): number => {
    return calculateMarkup(stockItem.unitPrice, stockItem.averageCost);
  }, []);

  /**
   * Calculate current inventory value for a stock item
   */
  const getCurrentInventoryValue = useCallback((
    stockItem: StockItem
  ): number => {
    return calculateInventoryValue(stockItem.physicalQuantity, stockItem.averageCost);
  }, []);

  /**
   * Get the current valuation method being used
   */
  const getValuationMethod = useCallback((): string => {
    return valuationMethod;
  }, [valuationMethod]);

  /**
   * Get the display name for the current valuation method
   */
  const getValuationMethodDisplayName = useCallback((): string => {
    switch (valuationMethod) {
      case 'average':
        return 'Average Cost (Weighted Average)';
      case 'fifo':
        return 'FIFO (First In, First Out)';
      case 'standard':
        return 'Standard Cost';
      default:
        return 'Unknown';
    }
  }, [valuationMethod]);

  return {
    processPurchaseReceipt,
    processSale,
    processAdjustment,
    processTransfer,
    getCurrentMargin,
    getCurrentMarkup,
    getCurrentInventoryValue,
    getValuationMethod,
    getValuationMethodDisplayName,
    valuationMethod, // Expose for direct access
  };
};
